import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';
import { TableRow } from '@material-ui/core';
import Swal from 'sweetalert2';
import * as Utils from '../../commons/utils/Utils';
import { StyledTableCellProps as StyledTableCell, StyledTooltip } from '../../commons/utils/TableStyles';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';

/**
 * La descripción del ítem es texto libre cargado por el usuario (ABM, importación masiva, ABM
 * legacy de Pañol2 e integraciones), y el modal de baja la inyecta con la opción `html` de
 * SweetAlert2, que interpreta HTML. Sin escapar sería XSS almacenado: el payload lo carga quien
 * puede editar ítems y lo ejecuta cualquier otro usuario al abrir la confirmación.
 *
 * La grilla no necesita esto: React escapa solo lo que se renderiza como JSX.
 */
const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const InventarioManoObraGridRow = (props) => {
  const intl = props.intl;
  const row = props.row;
  const baseUrl = props.baseUrl;
  const [redirectTo, setRedirectTo] = useState(null);

  const formatDecimal = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return LocaleFormat.formatNumber(Number(value), 2);
  };

  const handleDesactivar = () => {
    Swal.fire({
      title: intl.formatMessage({
        id: 'inventarioManoObra.desactivar.confirma',
        defaultMessage: '¿Confirma la desactivación?',
      }),
      html:
        intl.formatMessage({
          id: 'inventarioManoObra.desactivar.se_desactivara_el_item',
          defaultMessage: 'Se desactivará el ítem de mano de obra',
        }) +
        ': <b>' +
        escapeHtml(row.descripcion || '') +
        '</b><br/>' +
        intl.formatMessage({
          id: 'inventarioManoObra.desactivar.no_podra_seleccionarse',
          defaultMessage:
            'No podrá seleccionarse para consumo en nuevas Órdenes de Trabajo. Los consumos históricos no se modifican.',
        }),
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: intl.formatMessage({ id: 'yes', defaultMessage: 'Si' }),
      cancelButtonText: intl.formatMessage({ id: 'no', defaultMessage: 'No' }),
    }).then((result) => {
      if (result.value) {
        Utils.sendData('/inventario/mano-obra/' + row.id + '/desactivar', 'PUT', {})
          .then(() => {
            props.fetchRows();
            Swal(
              intl.formatMessage({
                id: 'inventarioManoObra.desactivar.desactivado',
                defaultMessage: 'Desactivado!',
              }),
              '',
              'success'
            );
          })
          .catch((err) => {
            Swal(err && err.getMessage ? err.getMessage() : 'Error', '', 'error');
          });
      }
    });
  };

  return (
    <>
      {redirectTo && <Redirect push to={redirectTo} />}
      <TableRow>
        <StyledTableCell align="left">{row.numeroDeParte || '-'}</StyledTableCell>
        {/*
          Estado activo/inactivo como franja lateral de color, que es como lo muestran las grillas
          del resto de la app (RepuestosGridRow, MotivosGridRow y otras 30). Antes se atenuaba la
          fila entera con opacity: se leía como "deshabilitado" y no como un estado, y no coincidía
          con nada del sistema.
        */}
        <td
          align="left"
          className={(row.activo ? 'status-green' : 'status-red') + ' p-0 all custom-lineHeight br'}
          style={{ maxWidth: '3px' }}
          title={
            row.activo
              ? intl.formatMessage({ id: 'inventarioManoObra.grid.estado_activo', defaultMessage: 'Activo' })
              : intl.formatMessage({ id: 'inventarioManoObra.grid.estado_inactivo', defaultMessage: 'Inactivo' })
          }
        />
        <StyledTableCell align="left">{row.descripcion}</StyledTableCell>
        <StyledTableCell align="left">{row.categoriaNombre || '-'}</StyledTableCell>
        <StyledTableCell align="left">{row.subcategoriaNombre || '-'}</StyledTableCell>
        <StyledTableCell align="left">{row.referencia || '-'}</StyledTableCell>
        <StyledTableCell align="right">{formatDecimal(row.costo)}</StyledTableCell>
        <StyledTableCell>
          <div className="d-inline-flex">
            {props.permissions.visualizar && (
              <button
                className="action view btn btn-sm btn-icon btn-dt-grid text-success"
                title={intl.formatMessage({ id: 'Ver', defaultMessage: 'Ver' })}
                data-toggle="tooltip"
                data-placement="top"
                onClick={() => setRedirectTo(baseUrl + '/' + row.id)}
              >
                <i className="fa fa-search fa-xs" />
              </button>
            )}
            {props.permissions.modificar && (
              <button
                className="action edit btn btn-sm btn-icon btn-dt-grid text-success"
                title={intl.formatMessage({ id: 'Editar', defaultMessage: 'Editar' })}
                data-toggle="tooltip"
                data-placement="top"
                onClick={() => setRedirectTo(baseUrl + '/' + row.id + '/edit')}
              >
                <i className="fa fa-pencil fa-xs" />
              </button>
            )}
            {/* Scenario 04: ver el historial es parte de ver el ítem, así que comparte el permiso. */}
            {props.permissions.visualizar && (
              <StyledTooltip
                title={intl.formatMessage({
                  id: 'inventarioManoObra.grid.ver_historial',
                  defaultMessage: 'Historial de modificaciones',
                })}
                arrow
                placement="top"
              >
                <button
                  className="action btn btn-sm btn-icon btn-dt-grid text-fleet"
                  onClick={() => props.onVerHistorial(row)}
                >
                  <i className="la la-history fa-xs" />
                </button>
              </StyledTooltip>
            )}
            {props.permissions.desactivar && row.activo ? (
              <button
                className="action delete btn btn-sm btn-icon btn-dt-grid text-danger"
                title={intl.formatMessage({ id: 'Desactivar', defaultMessage: 'Desactivar' })}
                data-toggle="tooltip"
                data-placement="top"
                onClick={() => handleDesactivar()}
              >
                <i className="fa fa-trash fa-xs" />
              </button>
            ) : (
              ''
            )}
          </div>
        </StyledTableCell>
      </TableRow>
    </>
  );
};

export default InventarioManoObraGridRow;
